import React from 'react';
import { createPortal } from 'react-dom';

interface SchedulerToggleModalProps {
  isOpen: boolean;
  currentState: boolean; // true = currently enabled, false = currently disabled
  onConfirm: () => void;
  onCancel: () => void;
}

function SchedulerToggleModal({
  isOpen,
  currentState,
  onConfirm,
  onCancel,
}: SchedulerToggleModalProps) {
  if (!isOpen) return null;

  const isEnabling = !currentState; // We're toggling, so if current is disabled, we're enabling

  return createPortal(
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEnabling ? 'Enable Scheduler' : 'Disable Scheduler'}</h2>
          <button 
            className="modal-close" 
            onClick={onCancel} 
            aria-label="Close dialog"
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
        <div className="modal-body">
          {isEnabling ? (
            <>
              <p>
                <strong>Enabling the scheduler will:</strong>
              </p>
              <ul>
                <li>Resume automatic task execution for upcoming publish events</li>
                <li>
                  Execute tasks related to upcoming publish events (content creation, media
                  generation, publishing)
                </li>
                <li>
                  Skip tasks that were scheduled in the past while the scheduler was off and are
                  not related to upcoming publish events
                </li>
              </ul>
              <p className="modal-note">
                <strong>Note:</strong> Tasks that were due in the past but are related to upcoming
                publish events will be executed. Other past tasks will need to be done manually.
              </p>
            </>
          ) : (
            <>
              <p>
                <strong>Disabling the scheduler will:</strong>
              </p>
              <ul>
                <li>Stop all automatic task execution</li>
                <li>Keep pending tasks in the queue (they won't be deleted)</li>
                <li>No content will be created, media generated, or posts published automatically</li>
              </ul>
              <p className="modal-note">
                <strong>Note:</strong> When you re-enable the scheduler, only tasks related to
                upcoming publish events will be executed. Tasks that became outdated while the
                scheduler was off will need to be done manually.
              </p>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={onConfirm}>
            {isEnabling ? 'Enable Scheduler' : 'Disable Scheduler'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default SchedulerToggleModal;
